import React from 'react';
import { motion } from 'framer-motion';
import { FadeIn, SectionLabel } from '../components/Shared';

const STEPS = [
    {
        n: '01',
        title: 'Discovery Call',
        when: 'Day 1',
        body: 'A free 15-minute call to understand your business, goals, and what you need online. No jargon, no pressure.',
    },
    {
        n: '02',
        title: 'Website Plan',
        when: 'Day 2–3',
        body: 'We create a detailed plan: pages, design direction, features, and timeline. You approve before we start building.',
    },
    {
        n: '03',
        title: 'Build & Secure',
        when: 'Day 4–10',
        body: "We build your website with security baked in from the start. SSL, firewall, backups — all configured.",
    },
    {
        n: '04',
        title: 'Review & Launch',
        when: 'Day 11–14',
        body: 'You review the site, request any changes, and we go live. Domain, hosting, DNS — all handled.',
    },
    {
        n: '05',
        title: 'Monitor & Grow',
        when: 'Ongoing',
        body: "Monthly security scans, uptime monitoring, performance reports, and ongoing support. We've got your back.",
    },
];

const Process = () => (
    <section className="relative py-24 overflow-hidden" data-testid="process-page">
        {/* Soft ambient cinematic glow to flow with the rest of the site */}
        <div
            className="absolute inset-0 -z-10 pointer-events-none"
            aria-hidden="true"
            style={{
                background:
                    'radial-gradient(40% 35% at 50% 0%, rgba(56,189,248,0.10) 0%, transparent 70%),' +
                    'radial-gradient(35% 30% at 12% 80%, rgba(99,102,241,0.10) 0%, transparent 70%),' +
                    'radial-gradient(30% 28% at 88% 90%, rgba(125,211,252,0.10) 0%, transparent 70%)',
            }}
        />

        <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <FadeIn>
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <SectionLabel>How we work</SectionLabel>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] leading-[1.15]">
                        From first call to launch in <span className="text-[#7B61FF]">14 days</span>.
                    </h2>
                    <p className="mt-4 text-[17px] text-[#4A4A5A] leading-relaxed">
                        Our simple, transparent process — no jargon, no surprises.
                    </p>
                </div>
            </FadeIn>

            {/* Vertical timeline */}
            <div className="relative pl-6 sm:pl-8">
                {/* Connecting line */}
                <div
                    className="absolute top-2 bottom-2 left-[7px] sm:left-[11px] w-px pointer-events-none"
                    style={{
                        background:
                            'linear-gradient(to bottom, rgba(37,99,235,0) 0%, rgba(37,99,235,0.20) 8%, rgba(37,99,235,0.20) 92%, rgba(37,99,235,0) 100%)',
                    }}
                    aria-hidden="true"
                />

                <ol className="space-y-6">
                    {STEPS.map((s, i) => (
                        <motion.li
                            key={s.n}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                            className="relative"
                        >
                            {/* Node bullet on the timeline */}
                            <span
                                className="absolute -left-6 sm:-left-8 top-7 w-4 h-4 rounded-full"
                                style={{
                                    background:
                                        'radial-gradient(closest-side, #38BDF8 0%, #2563EB 65%, rgba(37,99,235,0) 100%)',
                                    boxShadow:
                                        '0 0 0 4px rgba(255,255,255,0.7), 0 0 14px rgba(56,189,248,0.55)',
                                }}
                                aria-hidden="true"
                            />

                            <div
                                className="bg-white rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6"
                                data-testid={`process-step-${s.n}`}
                            >
                                {/* Big step number */}
                                <div
                                    className="font-display text-[3rem] leading-none font-extrabold tracking-tight shrink-0 select-none"
                                    style={{ color: 'rgba(37,99,235,0.30)' }}
                                    aria-hidden="true"
                                >
                                    {s.n}
                                </div>

                                {/* Title + body */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <h3 className="font-display text-[1.4rem] sm:text-[1.5rem] font-semibold text-[#1A1A2E] tracking-tight leading-tight">
                                            {s.title}
                                        </h3>
                                        {/* Time badge */}
                                        <span
                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold tracking-wide whitespace-nowrap"
                                            style={{
                                                backgroundColor: 'rgba(56,189,248,0.12)',
                                                color: '#0369A1',
                                                border: '1px solid rgba(56,189,248,0.25)',
                                                backdropFilter: 'blur(8px)',
                                                WebkitBackdropFilter: 'blur(8px)',
                                            }}
                                        >
                                            <span
                                                className="w-1.5 h-1.5 rounded-full"
                                                style={{ background: '#0EA5E9' }}
                                            />
                                            {s.when}
                                        </span>
                                    </div>
                                    <p className="mt-3 text-[0.95rem] text-[#4A4A5A] leading-relaxed">
                                        {s.body}
                                    </p>
                                </div>
                            </div>
                        </motion.li>
                    ))}
                </ol>
            </div>
        </div>
    </section>
);

export default Process;
